import { z } from "zod";
export const priceComplianceStatus = z.enum([
    "match", "mismatch", "promo_regular_mismatch", "unmatched", "unpriced", "unreadable",
]);
const detectionBox = z.object({
    x: z.number(), y: z.number(), width: z.number(), height: z.number(),
});
// Schema 2.0's per-field review surface (T2.1 parse.ts / T2.2 persist.ts):
// `value` is populated for `status` "ok" AND "unverified" (a readable but
// unconfirmed candidate worth surfacing for human review); every other
// status collapses `value` to null. `status` is kept as a lenient plain
// string rather than an enum — the vision pipeline's known statuses are
// ok | unverified | low_confidence | invalid | not_found | not_applicable,
// but this contract doesn't want to hard-fail on a new/unknown one.
export const priceTagFieldState = z.object({
    value: z.string().nullable(),
    status: z.string(),
});
export const priceTagComplianceInfo = z.object({
    id: z.string(),
    tagNumber: z.number().int(),
    parseStatus: z.enum(["parsed", "partial", "unparsed"]),
    tagType: z.string().nullable(), // promo | regular | unknown (open per vision §7)
    barcode: z.string().nullable(),
    matchedProductId: z.string().nullable(),
    productName: z.string().nullable(),
    shelfPrice: z.string().nullable(), // decimal string (selling_price)
    regularPrice: z.string().nullable(), // decimal string (promo tag's printed regular)
    expectedPrice: z.string().nullable(), // decimal string (DB)
    currency: z.string().nullable(),
    status: priceComplianceStatus,
    isPromotional: z.boolean(),
    delta: z.string().nullable(), // abs difference, decimal string
    deltaPct: z.number().nullable(),
    source: z.string().nullable(), // expected-price source, e.g. "catalog"
    ocrText: z.string().nullable(),
    bbox: detectionBox.nullable(),
    cropImageHash: z.string().nullable(),
    issueId: z.string().nullable(),
    // TM1: true when matchedProductId/productName/expectedPrice above came
    // from a read-time candidate lookup (pricing-compliance.service.ts) on a
    // barcode candidate (fieldStates.barcode.value, ok OR unverified) whose
    // snapshot had no stored match — never an authoritative automated-matcher
    // result. false for an ok-barcode compliance match already in the
    // snapshot, and false when no match/candidate was found at all. Additive;
    // defaults false so pre-existing fixtures/payloads without this key still
    // validate.
    matchIsCandidate: z.boolean().default(false),
    // T2.3: per-field {value,status} review surface, sourced from
    // price_tag_detections.metadata->'fieldStates' (T2.2). Additive —
    // the ok-only fields above (barcode/shelfPrice/regularPrice/etc.) and
    // the compliance `status` keep reading ONLY ok values; this is purely
    // for the UI to surface `unverified` (and other non-ok) candidates.
    fieldStates: z.object({
        barcode: priceTagFieldState,
        sellingPrice: priceTagFieldState,
        regularPrice: priceTagFieldState,
        priceLabel: priceTagFieldState,
        title: priceTagFieldState,
    }),
});
export const pricingComplianceSummary = z.object({
    tagsDetected: z.number().int(),
    parsed: z.number().int(),
    partial: z.number().int(),
    unparsed: z.number().int(),
    matched: z.number().int(),
    mismatches: z.number().int(),
    unmatched: z.number().int(),
    unpriced: z.number().int(),
    unreadable: z.number().int(),
    mismatchRate: z.number().nullable(), // mismatches / comparable, null if none comparable
});
export const capturePricingResponse = z.object({
    capture: z.object({
        id: z.string(), fileUrl: z.string().nullable(),
        widthPx: z.number().nullable(), heightPx: z.number().nullable(),
    }),
    hasVisionRun: z.boolean(),
    runStatus: z.string().nullable(),
    summary: pricingComplianceSummary,
    tags: z.array(priceTagComplianceInfo),
});
export const sessionPricingResponse = z.object({
    sessionId: z.string(),
    summary: pricingComplianceSummary,
    tags: z.array(priceTagComplianceInfo),
});
export const taskPricingResponse = z.object({
    taskId: z.string(),
    summary: pricingComplianceSummary,
    tags: z.array(priceTagComplianceInfo),
});
//# sourceMappingURL=pricing-compliance.schemas.js.map